import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchDoctorOPDAssessment } from "../../../store/slices/doctorOPDAssessmentsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function DoctorOPDPrint() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentDoctorOPDAssessment, isLoading, error } = useSelector((state) => state.doctorOPDAssessments);

  useEffect(() => {
    if (id) dispatch(fetchDoctorOPDAssessment(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentDoctorOPDAssessment) {
      const timer = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [currentDoctorOPDAssessment, navigate]);

  if (isLoading) return <div className="p-8 text-center">Loading...</div>;
  if (error) return <div className="p-8 text-danger text-center">{error}</div>;
  if (!currentDoctorOPDAssessment) return <div className="p-8 text-center">No record found.</div>;

  const a = currentDoctorOPDAssessment;
  const p = a.patient || {};
  const labs = a.labs || [];

  const fmtDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "—";

  const renderCheck = (condition) => (
    <span style={{ fontSize: '14px', marginRight: '5px' }}>{condition ? "☑" : "☐"}</span>
  );

  return (
    <>
      <style jsx global>{`
        * {
          box-sizing: border-box !important;
          font-family: Arial, sans-serif !important;
        }

        body {
          background: #fff !important;
          color: #000 !important;
          font-size: 11px !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .print-container {
          padding: 30px !important;
          position: relative !important;
        }
        ${printFooterStyles}

        .logo {
          position: absolute !important;
          top: 15px !important;
          left: 15px !important;
        }

        .logo img {
          height: 60px !important;
        }

        .header {
          text-align: center !important;
          margin-bottom: 20px !important;
          border-bottom: 2px solid #000 !important;
          padding-bottom: 10px !important;
        }

        .header h3 {
          font-size: 1.5rem !important;
          font-weight: bold !important;
        }

        .header h6 {
          font-size: 1rem !important;
          text-decoration: underline !important;
        }

        .section-title {
          background: #eee !important;
          padding: 4px 8px !important;
          font-weight: bold !important;
          margin: 15px 0 8px 0 !important;
          border: 1px solid #000 !important;
          text-transform: uppercase !important;
        }

        .info-table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-bottom: 10px !important;
        }

        .info-table td {
          border: 1px solid #000 !important;
          padding: 6px !important;
          vertical-align: top !important;
        }

        .label {
          font-weight: bold !important;
          width: 25% !important;
          background: #f9f9f9 !important;
        }

        .text-box {
          border: 1px solid #000 !important;
          padding: 8px !important;
          min-height: 50px !important;
          margin-top: 5px !important;
          white-space: pre-wrap !important;
        }

        .checkbox-grid {
          display: grid !important;
          grid-template-columns: repeat(3, 1fr) !important;
          gap: 5px !important;
          margin-bottom: 10px !important;
        }

        .checkbox-item {
          display: flex !important;
          align-items: center !important;
          margin-bottom: 4px !important;
        }

        @media print {
          .page-break {
            page-break-before: always !important;
          }
        }
      `}</style>

      <PrintWrapper>
        <div className="print-container">
          <div className="logo"><img src={logo} alt="TRH Logo" /></div>
          <div className="header">
            <h3>THE RECOVERY HOUSE</h3>
            <h6>Doctor OPD Assessment Report</h6>
          </div>

        {/* SECTION 1: PATIENT INFO */}
        <div className="section-title">Patient Information</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Patient Name</td>
              <td>{p.first_name} {p.last_name}</td>
              <td className="label">CNIC</td>
              <td>{p.cnic || "—"}</td>
            </tr>
            <tr>
              <td className="label">Gender</td>
              <td>{p.gender || "—"}</td>
              <td className="label">DOB</td>
              <td>{p.dob || "—"}</td>
            </tr>
            <tr>
              <td className="label">Education</td>
              <td>{a.education || "—"}</td>
              <td className="label">Comorbid</td>
              <td>{a.comorbid || "—"}</td>
            </tr>
            <tr>
              <td className="label">Consultant</td>
              <td>{a.consultant || "—"}</td>
              <td className="label">Reference</td>
              <td>{a.reference || "—"}</td>
            </tr>
            <tr>
              <td className="label">Duration of Illness</td>
              <td colSpan="3">{a.duration_of_illness || "—"}</td>
            </tr>
          </tbody>
        </table>

        {/* SECTION 2: ADMISSION REASON */}
        <div className="section-title">Reason for Admission & Consent</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', padding: '8px', border: '1px solid #000' }}>
          {[
            { k: 'reason_acute_care', l: 'Acute Care' },
            { k: 'reason_relapse', l: 'Relapse' },
            { k: 'reason_readmission_medical', l: 'Readmission After D/C' },
            { k: 'reason_respite_care', l: 'Respite Care' },
            { k: 'reason_diagnostic_evaluation', l: 'Diagnostic evaluation' },
            { k: 'reason_for_rehab', l: 'For Rehab' },
            { k: 'reason_clozapine_treatment', l: 'Clozapine Treatment' },
            { k: 'reason_chronic_management', l: 'Chronic Management' }
          ].map(item => (
            <div key={item.k} className="checkbox-item">
              {renderCheck(!!a[item.k])} {item.l}
            </div>
          ))}
        </div>
        <table className="info-table" style={{ marginTop: '-1px' }}>
          <tbody>
            <tr>
              <td className="label">Consent Taken From</td>
              <td>{a.consent_taken_from || "—"}</td>
            </tr>
          </tbody>
        </table>

        {/* SECTION 3: ESCORT */}
        <div className="section-title">Escort Details</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Escort Reason</td>
              <td>{a.escort_reason || "—"}</td>
            </tr>
            <tr>
              <td className="label">Guardian Name</td>
              <td>{a.guardian_name || "—"}</td>
              <td className="label">Relation</td>
              <td>{a.relation || "—"}</td>
            </tr>
          </tbody>
        </table>

        {/* SECTION 4: VITALS & EXAM */}
        <div className="section-title">Physical Examination & Vitals</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">BP</td><td>{a.blood_pressure || "—"} mmHg</td>
              <td className="label">Temp</td><td>{a.temperature || "—"} °F</td>
              <td className="label">Pulse</td><td>{a.pulse || "—"} bpm</td>
            </tr>
            <tr>
              <td className="label">RR</td><td>{a.respiratory_rate || "—"} bpm</td>
              <td className="label">Weight</td><td>{a.weight || "—"} kg</td>
              <td className="label">Height</td><td>{a.height || "—"} cm</td>
            </tr>
            <tr>
              <td className="label">BMI</td><td colSpan="3">{a.bmi || "—"}</td>
              <td className="label">RBS</td><td>{a.rbs || "—"} mg/dL</td>
            </tr>
          </tbody>
        </table>

        {/* SECTION 5: GENERAL APPEARANCE */}
        <div className="section-title">General Appearance (Systemic Review)</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Head</td><td>{a.ga_head || "—"}</td>
              <td className="label">Throat</td><td>{a.ga_throat || "—"}</td>
            </tr>
            <tr>
              <td className="label">Skin</td><td>{a.ga_skin || "—"}</td>
              <td className="label">Lymphatic</td><td>{a.ga_lymphatic || "—"}</td>
            </tr>
            <tr>
              <td className="label">Eyes</td><td>{a.ga_eyes || "—"}</td>
              <td className="label">Neck</td><td>{a.ga_neck || "—"}</td>
            </tr>
            <tr>
              <td className="label">Respiratory</td><td>{a.ga_respiratory || "—"}</td>
              <td className="label">Musculoskeletal</td><td>{a.ga_musculoskeletal || "—"}</td>
            </tr>
            <tr>
              <td className="label">Ear</td><td>{a.ga_ear || "—"}</td>
              <td className="label">Neurologic</td><td>{a.ga_neurologic || "—"}</td>
            </tr>
            <tr>
              <td className="label">Cardiovascular</td><td>{a.ga_cardiovascular || "—"}</td>
              <td className="label">Hygiene</td><td>{a.ga_hygiene || "—"}</td>
            </tr>
            <tr>
              <td className="label">Nose</td><td>{a.ga_nose || "—"}</td>
              <td className="label">Psychiatric</td><td>{a.ga_psychiatric || "—"}</td>
            </tr>
            <tr>
              <td className="label">Gastrointestinal</td><td colSpan="3">{a.ga_gastrointestinal || "—"}</td>
            </tr>
          </tbody>
        </table>

        <div className="page-break"></div>

        {/* SECTION 6: HISTORY */}
        <div className="section-title">Patient History</div>
        <div className="label" style={{ padding: '4px 8px', border: '1px solid #000', borderBottom: 'none' }}>Presenting Complaint</div>
        <div className="text-box">{a.presenting_complaint || "—"}</div>

        <div className="label" style={{ padding: '4px 8px', border: '1px solid #000', borderBottom: 'none' }}>History of Present Illness</div>
        <div className="text-box">{a.history_present_illness || "—"}</div>

        <table className="info-table">
          <tbody>
            <tr><td className="label">Past Medical History</td><td>{a.past_medical_history || "—"}</td></tr>
            <tr><td className="label">Past Psychiatric History</td><td>{a.past_psychiatric_history || "—"}</td></tr>
            <tr><td className="label">Past Surgical History</td><td>{a.past_surgical_history || "—"}</td></tr>
            <tr><td className="label">Family History</td><td>{a.family_history || "—"}</td></tr>
            <tr><td className="label">Social History</td><td>{a.social_history || "—"}</td></tr>
            <tr><td className="label">Allergies</td><td>{a.allergies || "—"}</td></tr>
          </tbody>
        </table>

        {/* SECTION 7: RISKS */}
        <div className="section-title">Risk Assessment</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">ROH (Homicidal)</td><td>{a.roh_risk || "—"}</td>
              <td className="label">ROS (Suicide)</td><td>{a.ros_risk || "—"}</td>
            </tr>
            <tr>
              <td className="label">ROV (Violence)</td><td>{a.rov_risk || "—"}</td>
              <td className="label">ROE (Escape)</td><td>{a.roe_risk || "—"}</td>
            </tr>
            <tr>
              <td className="label">ROF (Falls)</td><td>{a.rof_risk || "—"}</td>
              <td className="label">ROVU (Vulnerability)</td><td>{a.rovu_risk || "—"}</td>
            </tr>
          </tbody>
        </table>

        {/* SECTION 8: LABS & DIAGNOSIS */}
        <div className="section-title">Labs & Diagnosis</div>
        <table className="info-table">
          <thead>
            <tr>
              <th style={{ width: '10%' }}>#</th>
              <th>Lab Test Details</th>
            </tr>
          </thead>
          <tbody>
            {labs.length > 0 ? labs.map((lab, i) => (
              <tr key={i}><td>{i + 1}</td><td>{lab.test_name}</td></tr>
            )) : <tr><td colSpan="2" className="text-center">No labs listed</td></tr>}
          </tbody>
        </table>
        <table className="info-table" style={{ marginTop: '5px' }}>
          <tbody>
            <tr><td className="label">Provisional Diagnosis</td><td>{a.provisional_diagnosis || "—"}</td></tr>
            <tr><td className="label">Actual Diagnosis</td><td>{a.actual_diagnosis || "—"}</td></tr>
          </tbody>
        </table>

        {/* SECTION 9: TREATMENT PLAN */}
        <div className="section-title">Treatment Plan</div>
        <div className="label" style={{ padding: '4px 8px', border: '1px solid #000', borderBottom: 'none' }}>Pharmacotherapy</div>
        <div className="text-box">{a.pharmacotherapy || "—"}</div>

        <div className="label" style={{ padding: '4px 8px', border: '1px solid #000', borderBottom: 'none' }}>Special Orders</div>
        <div className="text-box">{a.special_order || "—"}</div>

        {/* REFERRAL */}
        <div className="section-title">Referral Info</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Is Referred?</td><td>{a.is_referred || "No"}</td>
              <td className="label">Service</td><td>{a.referral_service || "—"}</td>
            </tr>
          </tbody>
        </table>

        {/* SECTION 10: CONSULTANT REVIEW */}
        <div className="section-title">Consultant Review & Signatures</div>
        <div className="text-box" style={{ minHeight: '80px' }}>
          <strong>Consultant Note:</strong><br />
          {a.consultant_review || "—"}
        </div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Consultant Name</td><td>{a.consultant_name || "—"}</td>
              <td className="label">Date</td><td>{fmtDate(a.consultant_date)}</td>
            </tr>
            <tr>
              <td className="label">Signature / Stamp</td><td>{a.consultant_signature || " "}</td>
              <td className="label">Time</td><td>{a.consultant_time || "—"}</td>
            </tr>
          </tbody>
        </table>
      </div>
      </PrintWrapper>
    </>
  );
}
